import ReactDOM from 'react-dom';
import CustomAlert from '../component/alertMessage';

/**
 * AlertService - Manages application-wide alerts and notifications
 *
 * This service creates a container for alerts and provides methods to display
 * different types of alerts (success, error, warning) that automatically disappear
 * after a set duration.
 */
class AlertService {
  private container: HTMLDivElement;
  private readonly ALERT_DURATION = 2500; // Duration in milliseconds

  constructor() {
    this.container = document.createElement('div');
    this.setupContainerStyles();
    document.body.appendChild(this.container);
  }

  /**
   * Sets up the container styles for positioning alerts
   */
  private setupContainerStyles(): void {
    this.container.style.position = 'fixed';
    this.container.style.top = '20px';
    this.container.style.left = '50%';
    this.container.style.transform = 'translateX(-50%)';
    this.container.style.zIndex = '99999';
    this.container.style.display = 'flex';
    this.container.style.flexDirection = 'column';
    this.container.style.alignItems = 'center';
  }

  /**
   * Displays an alert with the specified type and message
   * @param type - The type of alert (success, error, warning)
   * @param message - The message to display
   */
  private show(type: 'success' | 'error' | 'warning', message: string): void {
    const alertDiv = document.createElement('div');
    this.container.appendChild(alertDiv);

    const removeAlert = () => {
      ReactDOM.unmountComponentAtNode(alertDiv);
      alertDiv.remove();
    };

    ReactDOM.render(<CustomAlert type={type} message={message} onClose={removeAlert} />, alertDiv);

    // Auto-remove after the specified duration
    setTimeout(removeAlert, this.ALERT_DURATION);
  }

  /**
   * Displays a success alert
   * @param message - The success message to display
   */
  success(message: string): void {
    this.show('success', message);
  }

  /**
   * Displays an error alert
   * @param message - The error message to display
   */
  error(message: string): void {
    this.show('error', message);
  }

  /**
   * Displays a warning alert
   * @param message - The warning message to display
   */
  warning(message: string): void {
    this.show('warning', message);
  }
}

// Export a singleton instance
export const alertService = new AlertService();
